import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Inbox,
  Lock,
  LogOut,
  Trash2,
  Mail,
  MailOpen,
  RefreshCw,
  ArrowLeft,
  Phone,
  Loader2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import logoMark from "../assets/logo-mark.png";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function AdminPage() {
  const [adminKey, setAdminKey] = useState(() => sessionStorage.getItem("fcy_admin_key") || "");
  const [keyInput, setKeyInput] = useState("");
  const [authed, setAuthed] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const headers = useCallback(
    (key) => ({ headers: { "X-Admin-Key": key || adminKey } }),
    [adminKey]
  );

  const fetchMessages = useCallback(
    async (key) => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/admin/messages`, headers(key));
        setMessages(res.data.messages || []);
        setUnread(res.data.unread || 0);
        return true;
      } catch (err) {
        if (err.response?.status === 401) {
          toast.error("Invalid admin key");
          sessionStorage.removeItem("fcy_admin_key");
          setAuthed(false);
          setAdminKey("");
        } else {
          toast.error("Failed to load messages");
        }
        return false;
      } finally {
        setLoading(false);
      }
    },
    [headers]
  );

  useEffect(() => {
    if (adminKey) {
      fetchMessages(adminKey).then((ok) => ok && setAuthed(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!keyInput.trim()) return;
    setAuthLoading(true);
    const ok = await fetchMessages(keyInput.trim());
    setAuthLoading(false);
    if (ok) {
      sessionStorage.setItem("fcy_admin_key", keyInput.trim());
      setAdminKey(keyInput.trim());
      setAuthed(true);
      toast.success("Welcome back");
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem("fcy_admin_key");
    setAdminKey("");
    setAuthed(false);
    setMessages([]);
    setKeyInput("");
  };

  const toggleRead = async (msg, read) => {
    try {
      await axios.put(`${API}/admin/messages/${msg.id}/read`, { read }, headers());
      setMessages((prev) => prev.map((m) => (m.id === msg.id ? { ...m, read } : m)));
      setUnread((prev) => (read ? Math.max(0, prev - 1) : prev + 1));
    } catch {
      toast.error("Failed to update message");
    }
  };

  const deleteMessage = async (msg) => {
    try {
      await axios.delete(`${API}/admin/messages/${msg.id}`, headers());
      setMessages((prev) => prev.filter((m) => m.id !== msg.id));
      if (!msg.read) setUnread((prev) => Math.max(0, prev - 1));
      toast.success("Message deleted");
    } catch {
      toast.error("Failed to delete message");
    }
  };

  const openMessage = (msg) => {
    setSelected(msg);
    if (!msg.read) toggleRead(msg, true);
  };

  const filtered = messages.filter((m) => {
    if (filter === "unread") return !m.read;
    if (filter === "read") return m.read;
    return true;
  });

  if (!authed) {
    return (
      <div className="min-h-screen bg-background hero-gradient grid-lines flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="glass-card w-full max-w-md p-8"
        >
          <div className="flex items-center gap-3 mb-6">
            <img src={logoMark} alt="FCY IT logo" className="h-11 w-11 object-contain" />
            <div>
              <h1 className="font-display text-xl font-semibold text-foreground">Admin Inbox</h1>
              <p className="text-sm text-muted-foreground">FCY IT Consultancy</p>
            </div>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <Input
              type="password"
              placeholder="Enter your admin key"
              value={keyInput}
              onChange={(e) => setKeyInput(e.target.value)}
              className="h-12 bg-white/5 border-white/10"
              data-testid="admin-passphrase-input"
            />
            <Button
              type="submit"
              disabled={authLoading || !keyInput.trim()}
              className="w-full h-12 rounded-xl font-medium"
              data-testid="admin-login-button"
            >
              {authLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Unlock Inbox"}
            </Button>
          </form>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            data-testid="admin-back-home-link"
          >
            <ArrowLeft className="h-4 w-4" /> Back to website
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-black/30 border-b border-white/10">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-primary/15 border border-primary/30 flex items-center justify-center">
              <Inbox className="h-4 w-4 text-primary" />
            </div>
            <div>
              <span className="font-display font-semibold text-foreground">Enquiry Inbox</span>
              <span className="ml-3 text-xs text-muted-foreground hidden sm:inline">
                {messages.length} total · {unread} unread
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => fetchMessages()}
              className="text-muted-foreground hover:text-foreground"
              data-testid="admin-refresh-button"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
            <Link to="/">
              <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground" data-testid="admin-home-button">
                <ArrowLeft className="h-4 w-4 mr-1" /> Site
              </Button>
            </Link>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="border-white/10 bg-white/5 hover:bg-white/10"
              data-testid="admin-logout-button"
            >
              <LogOut className="h-4 w-4 mr-1" /> Lock
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 sm:px-6 py-8">
        <Tabs value={filter} onValueChange={setFilter} className="mb-6">
          <TabsList className="bg-white/5 border border-white/10">
            <TabsTrigger value="all" data-testid="admin-filter-all">
              All ({messages.length})
            </TabsTrigger>
            <TabsTrigger value="unread" data-testid="admin-filter-unread">
              Unread ({unread})
            </TabsTrigger>
            <TabsTrigger value="read" data-testid="admin-filter-read">
              Read ({messages.length - unread})
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {loading && messages.length === 0 ? (
          <div className="glass-card p-12 flex items-center justify-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading enquiries...
          </div>
        ) : filtered.length === 0 ? (
          <div className="glass-card p-12 text-center" data-testid="admin-empty-state">
            <Inbox className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-foreground font-medium">No enquiries here</p>
            <p className="text-sm text-muted-foreground mt-1">
              {filter === "all"
                ? "When visitors send messages through the contact form, they will appear here."
                : `No ${filter} messages right now.`}
            </p>
          </div>
        ) : (
          <div className="glass-card overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead className="text-muted-foreground">Status</TableHead>
                  <TableHead className="text-muted-foreground">From</TableHead>
                  <TableHead className="text-muted-foreground hidden md:table-cell">Service</TableHead>
                  <TableHead className="text-muted-foreground hidden lg:table-cell">Received</TableHead>
                  <TableHead className="text-muted-foreground text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((msg) => (
                  <TableRow
                    key={msg.id}
                    className="border-white/10 cursor-pointer hover:bg-white/5"
                    onClick={() => openMessage(msg)}
                    data-testid={`admin-message-row-${msg.id}`}
                  >
                    <TableCell>
                      {msg.read ? (
                        <Badge variant="outline" className="border-white/15 text-muted-foreground">
                          Read
                        </Badge>
                      ) : (
                        <Badge className="bg-primary/15 text-primary border border-primary/30 hover:bg-primary/20">
                          New
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="font-medium text-foreground">{msg.name}</div>
                      <div className="text-xs text-muted-foreground">{msg.email}</div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-sm text-muted-foreground">
                      {msg.service}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-sm text-muted-foreground">
                      {formatDate(msg.created_at)}
                    </TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleRead(msg, !msg.read)}
                          className="text-muted-foreground hover:text-foreground h-8 w-8 p-0"
                          aria-label={msg.read ? "Mark as unread" : "Mark as read"}
                          data-testid={`admin-toggle-read-button-${msg.id}`}
                        >
                          {msg.read ? <Mail className="h-4 w-4" /> : <MailOpen className="h-4 w-4" />}
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-muted-foreground hover:text-destructive h-8 w-8 p-0"
                              aria-label="Delete message"
                              data-testid={`admin-inbox-delete-message-button-${msg.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="bg-card border-white/10">
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete this enquiry?</AlertDialogTitle>
                              <AlertDialogDescription>
                                The message from {msg.name} will be permanently removed. This cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel className="border-white/10 bg-white/5" data-testid="admin-delete-cancel-button">
                                Cancel
                              </AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => deleteMessage(msg)}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                data-testid="admin-delete-confirm-button"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </main>

      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="bg-card border-white/10 max-w-lg" data-testid="admin-message-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display">{selected.name}</DialogTitle>
                <DialogDescription className="space-y-1">
                  <span className="block">{selected.email}</span>
                  {selected.phone && (
                    <span className="flex items-center gap-1.5 text-sm">
                      <Phone className="h-3.5 w-3.5" /> {selected.phone}
                    </span>
                  )}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="outline" className="border-primary/30 text-primary">
                    {selected.service}
                  </Badge>
                  <span className="text-xs text-muted-foreground">{formatDate(selected.created_at)}</span>
                </div>
                <div className="rounded-xl bg-white/5 border border-white/10 p-4 text-sm text-foreground leading-relaxed whitespace-pre-wrap">
                  {selected.message}
                </div>
                <div className="flex gap-2">
                  <a href={`mailto:${selected.email}`} className="flex-1">
                    <Button className="w-full rounded-xl" data-testid="admin-reply-email-button">
                      <Mail className="h-4 w-4 mr-2" /> Reply by Email
                    </Button>
                  </a>
                  {selected.phone && (
                    <a href={`tel:${selected.phone}`}>
                      <Button variant="outline" className="rounded-xl border-white/10 bg-white/5" data-testid="admin-call-button">
                        <Phone className="h-4 w-4" />
                      </Button>
                    </a>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
